import axios from "axios";
import { installOffline } from "../fieldops/offline/interceptors";
import { api as fieldApi } from "../fieldops/lib/api";
import { drawingsOffline } from "./offlineMarkups";
import { beginCritical } from "../fieldops/offline/link";

export const DRW_API = `${window.location.origin}/api/drawings`;
export const FIELD_API = `${window.location.origin}/api/field`;

export const drw = axios.create({ baseURL: DRW_API });
installOffline("drawings", drw, drawingsOffline);   // FLD-OFF — markups + photo pins ride the same outbox; unsent ones stay drawn

drw.interceptors.request.use((c) => {
  const t = localStorage.getItem("auth_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const token = () => localStorage.getItem("auth_token") || "";

/** URLs that browsers fetch without headers (window.open, <img>) carry the token as ?auth=. */
export const sheetFileUrl = (sheetId) => `${DRW_API}/sheets/${sheetId}/file?auth=${token()}`;
export const photoUrl = (documentId) => `${DRW_API}/photo/${documentId}?auth=${token()}`;
export const sheetThumbUrl = (sheetId, width = 240) => `${DRW_API}/sheets/${sheetId}/thumb?width=${width}&auth=${token()}`;
export const verifyThumbUrl = (uploadId, part, page, width = 320) =>
  `${DRW_API}/uploads/${uploadId}/thumb?part=${part}&page=${page}&width=${width}&auth=${token()}`;
export const exportUrl = (kind, id, opts = {}) => {
  const q = new URLSearchParams({ auth: token(), ...Object.fromEntries(
    Object.entries(opts).map(([k, v]) => [k, String(v)])) });
  return `${DRW_API}/${kind}/${id}/export?${q}`;
};

export const fetchSheetPdf = async (sheetId) => {
  // DRW-LOAD-C — a sheet is up to 12MB, but 20s of silence is already a failure: the interceptor
  // retries with backoff rather than sitting on a dead request for a minute.
  const r = await drw.get(`/sheets/${sheetId}/file`, { responseType: "arraybuffer", timeout: 20000, _heavy: true, _critical: true });
  return r.data;
};

export const uploadCompletionPhoto = async (projectId, file, uploader) => {
  const fd = new FormData();
  fd.append("project_id", projectId);
  fd.append("uploader", uploader || "");
  fd.append("file", file);
  const r = await fieldApi.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });   // field client → queues offline, `local:` ref resolved at replay
  return r.data;
};

/** DRW-FIX — a set is uploaded in 5MB chunks so no single request can be refused for size, and a
 *  chunk that fails is simply resent (the server overwrites that index). Nothing is queued to the
 *  offline outbox: an ingest needs the server, so `_noQueue` lets the error surface instead. */
export const setLimits = () => drw.get("/uploads/limits", { timeout: 15000, _heavy: true }).then((r) => r.data);

/** FLD-OFF-FIX-3 §2/§6 — an ingest is a server-dependent operation: `_critical` says a REAL
 *  network failure of this request counts, `_heavy` says its own slowness never does, and the
 *  `beginCritical()` window stops any OTHER request's failure from putting the app offline while
 *  the upload is running. `_noQueue` keeps it out of the outbox (an ingest needs the server). */
const RAW = { _noQueue: true, timeout: 120000, _critical: true, _heavy: true };
const HARD_CAP_MS = 180000;        // §6 — nothing in an upload spins for ever

export async function uploadSet({ projectId, files, form, onProgress }) {
  const done = beginCritical(180000);   // an upload may legitimately run for minutes
  try {
    return await runUpload({ projectId, files, form, onProgress });
  } finally { done(); }
}

async function runUpload({ projectId, files, form, onProgress }) {
  const { data: init } = await drw.post(`/projects/${projectId}/uploads`,
    { files: files.map((f) => ({ file_name: f.name, size: f.size })), ...form }, RAW);
  const size = init.chunk_size;
  const total = init.parts.reduce((n, p) => n + p.chunks_total, 0);
  let sent = 0;
  for (const part of init.parts) {
    const file = files[part.index];
    for (let i = 0; i < part.chunks_total; i++) {
      const fd = new FormData();
      fd.append("file", file.slice(i * size, Math.min(file.size, (i + 1) * size)), `c${i}`);
      let err = null;
      const chunkStart = Date.now();
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          await drw.put(`/uploads/${init.upload_id}/chunks`, fd, { ...RAW, params: { part: part.index, index: i } });
          err = null;
          break;
        } catch (e) {
          err = e;
          // §6 — a hard upper bound, then a real message. No piece retries for ever.
          if (Date.now() - chunkStart > HARD_CAP_MS) {
            throw Object.assign(new Error(`Piece ${i + 1} of ${part.chunks_total} could not be uploaded after ${Math.round((Date.now() - chunkStart) / 1000)}s — ${errText(e, "the server did not accept it")}. Nothing was filed; try the upload again.`), { cause: e });
          }
          onProgress?.({ phase: "retry", done: sent, total, attempt: attempt + 1 });
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        }
      }
      if (err) throw err;
      onProgress?.({ phase: "upload", done: ++sent, total });
    }
  }
  await drw.post(`/uploads/${init.upload_id}/complete`, {}, RAW);
  return init.upload_id;
}

export const waitingVerification = (projectId) =>
  drw.get(`/projects/${projectId}/uploads/waiting`, { _heavy: true }).then((r) => r.data.waiting);
export const uploadStatus = (uploadId) => drw.get(`/uploads/${uploadId}`, { ...RAW, timeout: 10000 }).then((r) => r.data);
/** The ingest poll window: while the server is reading the PDF, nothing else may declare the
 *  link dead. Returns the closer. */
export const beginIngestWatch = () => beginCritical(180000);

export const errText = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  return d.detail || d.code || fallback;
};

export const STATE_COLORS = {
  installed: "#16a34a", on_site: "#eab308", on_hand: "#2563eb", surplus: "#9333ea",
  not_requested: "#6b7280", unlinked: "#f59e0b",
};
export const STATE_LABELS = {
  installed: "Installed", on_site: "On site", on_hand: "On hand", surplus: "Surplus",
  not_requested: "Not requested", unlinked: "Which FRS item is this?",
};
export const LAYER_COLORS = { "Pre-Con": "#15803d", Ops: "#1d4ed8", Field: "#b91c1c" };

// DRW-FIX-3 — the signed-in person and the delete rule live in one dependency-free module so
// they can be checked outside a browser and cannot drift between the two viewers.
export { signedIn, emailOf, fieldRoleOf, isFieldUser, canDeleteMarkup } from "./permissions";

